import React, { useCallback, useEffect, useState } from 'react'
import { api, getApiErrorMessage } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'

const VERSION_TABS = [
    { id: 'external', label: '카카오 단체발송' },
    { id: 'mobile', label: '모바일앱' },
]

const APP_PLATFORM_OPTIONS = [
    { value: 'ANDROID', label: 'Android' },
    { value: 'IOS', label: 'iOS' },
]

function platformLabel(platform) {
    return APP_PLATFORM_OPTIONS.find(option => option.value === platform)?.label || platform || '-'
}

function parseOptionalPositiveInt(value) {
    if (value === null || value === undefined || value === '') return null
    const parsed = parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
}

export default function ExternalProgramVersionsPage() {
    const { hasAnyRole } = useAuth()
    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
    const [activeTab, setActiveTab] = useState('external')
    const [versions, setVersions] = useState([])
    const [appPolicies, setAppPolicies] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [programCodeFilter, setProgramCodeFilter] = useState('KAKAO_SENDER')
    const [appPlatformFilter, setAppPlatformFilter] = useState('')
    const [editOpen, setEditOpen] = useState(false)
    const [editVersion, setEditVersion] = useState(null)
    const [createOpen, setCreateOpen] = useState(false)
    const [appEditOpen, setAppEditOpen] = useState(false)
    const [appEditPolicy, setAppEditPolicy] = useState(null)
    const [appCreateOpen, setAppCreateOpen] = useState(false)
    const { show } = useToast()

    const loadVersions = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const params = programCodeFilter ? { programCode: programCodeFilter } : {}
            const res = await api.get('/admin/external/versions', { params })
            const data = res?.data?.data ?? res?.data ?? {}
            const items = data.items || []
            // 날짜 내림차순 정렬 (최신 버전이 위로)
            const sortedItems = Array.isArray(items) ? [...items].sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
                return dateB - dateA // 내림차순
            }) : []
            setVersions(sortedItems)
        } catch (e) {
            setError(getApiErrorMessage(e, '버전 목록을 불러오지 못했습니다'))
        } finally {
            setLoading(false)
        }
    }, [programCodeFilter])

    const loadAppPolicies = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const params = appPlatformFilter ? { platform: appPlatformFilter } : {}
            const res = await api.get('/admin/app-version-policies', { params })
            const data = res?.data?.data ?? res?.data ?? {}
            setAppPolicies(Array.isArray(data.items) ? data.items : [])
        } catch (e) {
            setError(getApiErrorMessage(e, '앱 버전 정책을 불러오지 못했습니다.'))
        } finally {
            setLoading(false)
        }
    }, [appPlatformFilter])

    useEffect(() => {
        if (!isOperator) return
        if (activeTab === 'mobile') {
            loadAppPolicies()
        } else {
            loadVersions()
        }
    }, [isOperator, activeTab, loadAppPolicies, loadVersions])

    const onDelete = async (version) => {
        if (!window.confirm(`정말 삭제하시겠습니까?\n프로그램: ${version.programCode}\n버전: ${version.version}`)) return
        try {
            await api.delete(`/admin/external/versions/${version.id}`)
            show('삭제되었습니다', 'success')
            loadVersions()
        } catch (err) {
            show(getApiErrorMessage(err, '삭제에 실패했습니다.'), 'error')
        }
    }

    const onDeleteAppPolicy = async (policy) => {
        const label = `${platformLabel(policy.platform)} ${policy.versionName}+${policy.buildNumber}`
        if (!window.confirm(`앱 버전 정책을 삭제하시겠습니까?\n${label}`)) return
        try {
            await api.delete(`/admin/app-version-policies/${policy.id}`)
            show('삭제되었습니다', 'success')
            loadAppPolicies()
        } catch (err) {
            show(getApiErrorMessage(err, '삭제에 실패했습니다.'), 'error')
        }
    }

    const onEdit = (version) => {
        setEditVersion(version)
        setEditOpen(true)
    }

    const onCreate = () => {
        setEditVersion(null)
        setCreateOpen(true)
    }

    const onCreateAppPolicy = () => {
        setAppEditPolicy(null)
        setAppCreateOpen(true)
    }

    const onSave = async (formData) => {
        try {
            console.log('버전 저장 요청:', { editVersion, formData })
            if (editVersion) {
                const res = await api.patch(`/admin/external/versions/${editVersion.id}`, formData)
                console.log('버전 수정 응답:', res?.data)
                show('수정되었습니다', 'success')
            } else {
                const res = await api.post('/admin/external/versions', formData)
                console.log('버전 생성 응답:', res?.data)
                show('생성되었습니다', 'success')
            }
            setEditOpen(false)
            setCreateOpen(false)
            setEditVersion(null)
            loadVersions()
        } catch (err) {
            show(getApiErrorMessage(err, (editVersion ? '수정' : '생성') + '에 실패했습니다.'), 'error')
        }
    }

    const onSaveAppPolicy = async (formData) => {
        try {
            if (appEditPolicy) {
                await api.patch(`/admin/app-version-policies/${appEditPolicy.id}`, formData)
                show('수정되었습니다', 'success')
            } else {
                await api.post('/admin/app-version-policies', formData)
                show('생성되었습니다', 'success')
            }
            setAppEditOpen(false)
            setAppCreateOpen(false)
            setAppEditPolicy(null)
            loadAppPolicies()
        } catch (err) {
            show(getApiErrorMessage(err, (appEditPolicy ? '수정' : '생성') + '에 실패했습니다.'), 'error')
        }
    }

    const formatFileSize = (bytes) => {
        if (!bytes) return '-'
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
    }

    if (!isOperator) {
        return <div className="p-4">운영자 권한이 필요합니다.</div>
    }

    const header = (
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
                <input
                    type="text"
                    placeholder="프로그램 코드 필터 (예: KAKAO_SENDER)"
                    className="rounded border px-3 py-2 text-sm"
                    value={programCodeFilter}
                    onChange={(e) => setProgramCodeFilter(e.target.value)}
                />
            </div>
            <button
                className="rounded bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800"
                onClick={onCreate}
            >
                + 버전 추가
            </button>
        </div>
    )

    const appHeader = (
        <div className="flex items-center justify-between mb-4">
            <select
                className="rounded border px-3 py-2 text-sm"
                value={appPlatformFilter}
                onChange={(e) => setAppPlatformFilter(e.target.value)}
            >
                <option value="">전체 플랫폼</option>
                {APP_PLATFORM_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <button
                className="rounded bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800"
                onClick={onCreateAppPolicy}
            >
                + 앱 버전 추가
            </button>
        </div>
    )

    const table = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">프로그램 코드</th>
                    <th className="px-4 py-2 text-left">버전</th>
                    <th className="px-4 py-2 text-left">다운로드 URL</th>
                    <th className="px-4 py-2 text-left">파일 크기</th>
                    <th className="px-4 py-2 text-left">필수 업데이트</th>
                    <th className="px-4 py-2 text-left">활성화</th>
                    <th className="px-4 py-2 text-left">배포일</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {versions.map(v => (
                    <tr key={v.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-2 font-mono text-xs">{v.programCode}</td>
                        <td className="px-4 py-2 font-mono">{v.version}</td>
                        <td className="px-4 py-2">
                            <a href={v.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-xs block">
                                {v.downloadUrl}
                            </a>
                        </td>
                        <td className="px-4 py-2">{formatFileSize(v.fileSize)}</td>
                        <td className="px-4 py-2">
                            {v.isCritical ? (
                                <span className="text-red-600 font-semibold">필수</span>
                            ) : (
                                <span className="text-slate-400">-</span>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            {v.isActive ? (
                                <span className="text-green-600">활성</span>
                            ) : (
                                <span className="text-slate-400">비활성</span>
                            )}
                        </td>
                        <td className="px-4 py-2">{formatDateTimeMinute(v.createdAt)}</td>
                        <td className="px-4 py-2 text-right">
                            <button className="text-brand-navy hover:underline mr-3" onClick={() => onEdit(v)}>수정</button>
                            <button className="text-red-600 hover:underline" onClick={() => onDelete(v)}>삭제</button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const appTable = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">플랫폼</th>
                    <th className="px-4 py-2 text-left">버전명</th>
                    <th className="px-4 py-2 text-left">빌드 번호</th>
                    <th className="px-4 py-2 text-left">최소 지원</th>
                    <th className="px-4 py-2 text-left">업데이트</th>
                    <th className="px-4 py-2 text-left">활성화</th>
                    <th className="px-4 py-2 text-left">스토어 URL</th>
                    <th className="px-4 py-2 text-left">수정일</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {appPolicies.map(policy => (
                    <tr key={policy.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-2">{platformLabel(policy.platform)}</td>
                        <td className="px-4 py-2 font-mono">{policy.versionName}</td>
                        <td className="px-4 py-2 font-mono">{policy.buildNumber}</td>
                        <td className="px-4 py-2">
                            {policy.minSupportedVersionName || policy.minSupportedBuildNumber ? (
                                <span className="font-mono">
                                    {policy.minSupportedVersionName || '-'}
                                    {policy.minSupportedBuildNumber ? `+${policy.minSupportedBuildNumber}` : ''}
                                </span>
                            ) : (
                                <span className="text-slate-400">-</span>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            {policy.forceUpdate ? (
                                <span className="font-semibold text-red-600">필수</span>
                            ) : (
                                <span className="text-slate-600">선택</span>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            {policy.isActive ? (
                                <span className="text-green-600">활성</span>
                            ) : (
                                <span className="text-slate-400">비활성</span>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            <a href={policy.storeUrl} target="_blank" rel="noopener noreferrer" className="block max-w-xs truncate text-blue-600 hover:underline">
                                {policy.storeUrl}
                            </a>
                        </td>
                        <td className="px-4 py-2">{formatDateTimeMinute(policy.updatedAt || policy.createdAt)}</td>
                        <td className="px-4 py-2 text-right">
                            <button
                                className="mr-3 text-brand-navy hover:underline"
                                onClick={() => {
                                    setAppEditPolicy(policy)
                                    setAppEditOpen(true)
                                }}
                            >
                                수정
                            </button>
                            <button className="text-red-600 hover:underline" onClick={() => onDeleteAppPolicy(policy)}>삭제</button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const tabs = (
        <div className="mb-4 flex gap-2 border-b border-slate-200">
            {VERSION_TABS.map(tab => (
                <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`border-b-2 px-4 py-2 text-sm font-semibold ${
                        activeTab === tab.id
                            ? 'border-brand-navy text-brand-navy'
                            : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    )

    return (
        <section>
            <div className="flex items-center justify-between mb-4">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold">버전관리</h1>
                    <MenuGuideButton slug="ops-management" title="운영관리 가이드" anchor="step-03" />
                </div>
            </div>

            {tabs}

            <ListContainer
                loading={loading}
                error={error}
                onRetry={activeTab === 'mobile' ? loadAppPolicies : loadVersions}
                header={activeTab === 'mobile' ? appHeader : header}
                table={activeTab === 'mobile' ? appTable : table}
                footer={null}
            />

            {(editOpen || createOpen) && (
                <VersionEditModal
                    version={editVersion}
                    onClose={() => {
                        setEditOpen(false)
                        setCreateOpen(false)
                        setEditVersion(null)
                    }}
                    onSave={onSave}
                />
            )}

            {(appEditOpen || appCreateOpen) && (
                <AppVersionPolicyModal
                    policy={appEditPolicy}
                    onClose={() => {
                        setAppEditOpen(false)
                        setAppCreateOpen(false)
                        setAppEditPolicy(null)
                    }}
                    onSave={onSaveAppPolicy}
                />
            )}
        </section>
    )
}

function VersionEditModal({ version, onClose, onSave }) {
    const { show } = useToast()
    const initialProgramCode = version?.programCode || ''
    const [formData, setFormData] = useState({
        programCode: initialProgramCode,
        version: version?.version || '',
        downloadUrl: version?.downloadUrl || '',
        releaseNotes: version?.releaseNotes || '',
        isCritical: version?.isCritical || false,
        isActive: version?.isActive !== undefined ? version.isActive : true,
        minSupportedVersion: version?.minSupportedVersion || '',
        fileSize: version?.fileSize || '',
        checksum: version?.checksum || '',
        installationManualFileId: version?.installationManualFileId || '',
        usageManualFileId: version?.usageManualFileId || '',
    })
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [selectedFile, setSelectedFile] = useState(null)
    const [uploadingManual, setUploadingManual] = useState({ installation: false, usage: false })
    const [programCodes, setProgramCodes] = useState([])
    const [useCustomProgramCode, setUseCustomProgramCode] = useState(false)

    const loadProgramCodes = useCallback(async () => {
        try {
            const res = await api.get('/admin/external/versions/program-codes')
            const codes = res?.data?.data ?? res?.data ?? []
            setProgramCodes(Array.isArray(codes) ? codes : [])
            // 기존 프로그램 코드가 목록에 없으면 직접 입력 모드로 설정
            if (initialProgramCode && !codes.includes(initialProgramCode)) {
                setUseCustomProgramCode(true)
            }
        } catch (e) {
            console.error('프로그램 코드 목록 조회 실패:', e)
        }
    }, [initialProgramCode])

    useEffect(() => {
        if (!version) {
            // 새 버전 생성 시에만 프로그램 코드 목록 로드
            loadProgramCodes()
        } else {
            // 수정 시에는 기존 코드만 사용
            setProgramCodes([])
            setUseCustomProgramCode(false)
        }
    }, [version, loadProgramCodes])

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return

        setSelectedFile(file)
        setUploading(true)
        setUploadProgress(0)

        try {
            // 파일 업로드
            const formData = new FormData()
            formData.append('file', file)
            
            const uploadRes = await api.post('/files/upload/external-program', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        setUploadProgress(percent)
                    }
                }
            })

            const fileId = uploadRes?.data?.data?.fileId || uploadRes?.data?.fileId
            if (!fileId) {
                throw new Error('파일 ID를 받지 못했습니다')
            }

            // 공유 토큰 생성 (만료 없음 - 설치 파일은 영구 사용)
            const shareRes = await api.post(`/files/${fileId}/share`, {
                // expiresAt을 보내지 않으면 만료 없이 생성됨
            })

            const shareUrl = shareRes?.data?.data?.url
            if (!shareUrl) {
                throw new Error('공유 URL을 생성하지 못했습니다')
            }

            // 폼 데이터 자동 채우기
            setFormData(prev => ({
                ...prev,
                downloadUrl: shareUrl,
                fileSize: file.size.toString()
            }))

            show('파일 업로드 완료', 'success')
            // 업로드 성공 후 파일 선택 유지 (체크섬 계산용)
        } catch (err) {
            console.error('파일 업로드 실패:', err)
            const message = err?.response?.data?.message || err.message || '파일 업로드 실패'
            show('파일 업로드 실패: ' + message, 'error')
            setSelectedFile(null)
        } finally {
            setUploading(false)
            setUploadProgress(0)
        }
    }

    const calculateChecksum = async (file) => {
        try {
            const buffer = await file.arrayBuffer()
            const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
            setFormData(prev => ({ ...prev, checksum: hashHex }))
            show('체크섬 계산 완료', 'success')
        } catch (err) {
            console.error('체크섬 계산 실패:', err)
            show('체크섬 계산 실패', 'error')
        }
    }

    const handleManualFileUpload = async (type, file) => {
        if (!file) return

        setUploadingManual(prev => ({ ...prev, [type]: true }))

        try {
            const formData = new FormData()
            formData.append('file', file)
            
            const uploadRes = await api.post('/files/upload/external-program', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            const fileId = uploadRes?.data?.data?.fileId || uploadRes?.data?.fileId
            if (!fileId) {
                throw new Error('파일 ID를 받지 못했습니다')
            }

            // 공유 토큰 생성 (만료 없음)
            const shareRes = await api.post(`/files/${fileId}/share`, {})

            const shareUrl = shareRes?.data?.data?.url
            if (!shareUrl) {
                throw new Error('공유 URL을 생성하지 못했습니다')
            }

            // 폼 데이터에 파일 ID 저장
            const fieldName = type === 'installation' ? 'installationManualFileId' : 'usageManualFileId'
            setFormData(prev => ({
                ...prev,
                [fieldName]: fileId
            }))

            show(`${type === 'installation' ? '설치' : '사용법'} 가이드 업로드 완료`, 'success')
        } catch (err) {
            console.error('매뉴얼 파일 업로드 실패:', err)
            const message = err?.response?.data?.message || err.message || '파일 업로드 실패'
            show('매뉴얼 파일 업로드 실패: ' + message, 'error')
        } finally {
            setUploadingManual(prev => ({ ...prev, [type]: false }))
        }
    }

    const handleClose = () => {
        setSelectedFile(null)
        onClose()
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        const data = {
            ...formData,
            fileSize: formData.fileSize ? parseInt(formData.fileSize) : null,
            // isActive가 undefined이면 기본값 true로 설정 (새 버전 생성 시)
            isActive: formData.isActive !== undefined ? formData.isActive : (!version ? true : formData.isActive),
        }
        // 빈 문자열을 null로 변환
        if (!data.minSupportedVersion) data.minSupportedVersion = null
        if (!data.checksum) data.checksum = null
        if (!data.releaseNotes) data.releaseNotes = null
        // 매뉴얼 파일 ID도 빈 문자열을 null로 변환
        if (!data.installationManualFileId || data.installationManualFileId.trim() === '') {
            data.installationManualFileId = null
        }
        if (!data.usageManualFileId || data.usageManualFileId.trim() === '') {
            data.usageManualFileId = null
        }

        if (version) {
            // 수정 시에는 programCode와 version은 제외
            delete data.programCode
            delete data.version
        }
        
        console.log('저장할 데이터:', data)
        onSave(data)
    }

    return (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
            <form onSubmit={handleSubmit} className="w-full max-w-2xl rounded bg-white p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-lg font-semibold">{version ? '버전 수정' : '버전 추가'}</h2>

                {!version && (
                    <>
                        <div>
                            <label className="block text-sm font-medium mb-1">프로그램 코드 *</label>
                            {programCodes.length > 0 && (
                                <div className="mb-2">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={useCustomProgramCode}
                                            onChange={(e) => {
                                                setUseCustomProgramCode(e.target.checked)
                                                if (!e.target.checked && programCodes.length > 0) {
                                                    setFormData(prev => ({ ...prev, programCode: programCodes[0] }))
                                                } else {
                                                    setFormData(prev => ({ ...prev, programCode: '' }))
                                                }
                                            }}
                                        />
                                        <span>직접 입력</span>
                                    </label>
                                </div>
                            )}
                            {!useCustomProgramCode && programCodes.length > 0 ? (
                                <select
                                    required
                                    className="mt-1 w-full rounded border px-3 py-2"
                                    value={formData.programCode}
                                    onChange={(e) => setFormData({ ...formData, programCode: e.target.value })}
                                >
                                    <option value="">선택하세요</option>
                                    {programCodes.map(code => (
                                        <option key={code} value={code}>{code}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    required
                                    className="mt-1 w-full rounded border px-3 py-2"
                                    value={formData.programCode}
                                    onChange={(e) => setFormData({ ...formData, programCode: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                                    placeholder="KAKAO_SENDER"
                                />
                            )}
                            {programCodes.length > 0 && !useCustomProgramCode && (
                                <p className="mt-1 text-xs text-slate-500">
                                    기존 프로그램 코드를 선택하거나 직접 입력 옵션을 사용하세요.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">버전 *</label>
                            <input
                                type="text"
                                required
                                className="mt-1 w-full rounded border px-3 py-2"
                                value={formData.version}
                                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                                placeholder="1.0.1"
                            />
                        </div>
                    </>
                )}

                <div>
                    <label className="block text-sm font-medium mb-1">설치 파일 업로드</label>
                    <div className="mt-1 space-y-2">
                        <div className="flex items-center gap-2">
                            <input
                                type="file"
                                accept=".exe,.msi,.zip"
                                onChange={handleFileSelect}
                                disabled={uploading}
                                className="text-sm"
                            />
                            {selectedFile && !uploading && (
                                <button
                                    type="button"
                                    onClick={() => calculateChecksum(selectedFile)}
                                    className="text-xs text-brand-navy hover:underline"
                                >
                                    체크섬 계산
                                </button>
                            )}
                        </div>
                        {uploading && (
                            <div className="space-y-1">
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div
                                        className="bg-brand-navy h-2 rounded-full transition-all"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                                <div className="text-xs text-slate-600">업로드 중... {uploadProgress}%</div>
                            </div>
                        )}
                        {selectedFile && !uploading && (
                            <div className="text-xs text-slate-600">
                                선택된 파일: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">다운로드 URL *</label>
                    <input
                        type="url"
                        required
                        className="mt-1 w-full rounded border px-3 py-2"
                        value={formData.downloadUrl}
                        onChange={(e) => setFormData({ ...formData, downloadUrl: e.target.value })}
                        placeholder="파일 업로드 또는 직접 URL 입력"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                        파일을 업로드하면 자동으로 채워집니다. 또는 외부 URL을 직접 입력할 수 있습니다.
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">릴리스 노트</label>
                    <textarea
                        className="mt-1 w-full rounded border px-3 py-2"
                        rows="4"
                        value={formData.releaseNotes}
                        onChange={(e) => setFormData({ ...formData, releaseNotes: e.target.value })}
                        placeholder="변경 사항을 입력하세요..."
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">파일 크기 (bytes)</label>
                        <input
                            type="number"
                            className="mt-1 w-full rounded border px-3 py-2"
                            value={formData.fileSize}
                            onChange={(e) => setFormData({ ...formData, fileSize: e.target.value })}
                            placeholder="15728640"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">체크섬 (SHA256)</label>
                        <input
                            type="text"
                            className="mt-1 w-full rounded border px-3 py-2 font-mono text-xs"
                            value={formData.checksum}
                            onChange={(e) => setFormData({ ...formData, checksum: e.target.value })}
                            placeholder="a1b2c3d4..."
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">최소 지원 버전</label>
                    <input
                        type="text"
                        className="mt-1 w-full rounded border px-3 py-2"
                        value={formData.minSupportedVersion}
                        onChange={(e) => setFormData({ ...formData, minSupportedVersion: e.target.value })}
                        placeholder="1.0.0"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">설치 가이드 파일</label>
                        <div className="space-y-2">
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => {
                                    const file = e.target.files?.[0]
                                    if (file) handleManualFileUpload('installation', file)
                                }}
                                disabled={uploadingManual.installation}
                                className="text-sm"
                            />
                            {formData.installationManualFileId && (
                                <div className="text-xs text-slate-600">
                                    파일 ID: {formData.installationManualFileId}
                                </div>
                            )}
                            {uploadingManual.installation && (
                                <div className="text-xs text-slate-600">업로드 중...</div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">사용법 가이드 파일</label>
                        <div className="space-y-2">
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => {
                                    const file = e.target.files?.[0]
                                    if (file) handleManualFileUpload('usage', file)
                                }}
                                disabled={uploadingManual.usage}
                                className="text-sm"
                            />
                            {formData.usageManualFileId && (
                                <div className="text-xs text-slate-600">
                                    파일 ID: {formData.usageManualFileId}
                                </div>
                            )}
                            {uploadingManual.usage && (
                                <div className="text-xs text-slate-600">업로드 중...</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.isCritical}
                            onChange={(e) => setFormData({ ...formData, isCritical: e.target.checked })}
                        />
                        <span className="text-sm">필수 업데이트</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        <span className="text-sm">활성화</span>
                    </label>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="rounded border px-4 py-2 text-sm" onClick={handleClose}>
                        취소
                    </button>
                    <button type="submit" className="rounded bg-brand-navy text-white px-4 py-2 text-sm">
                        {version ? '수정' : '생성'}
                    </button>
                </div>
            </form>
        </div>
    )
}

function AppVersionPolicyModal({ policy, onClose, onSave }) {
    const [formData, setFormData] = useState({
        platform: policy?.platform || 'ANDROID',
        versionName: policy?.versionName || '',
        buildNumber: policy?.buildNumber || '',
        minSupportedVersionName: policy?.minSupportedVersionName || '',
        minSupportedBuildNumber: policy?.minSupportedBuildNumber || '',
        storeUrl: policy?.storeUrl || '',
        releaseNotes: policy?.releaseNotes || '',
        forceUpdate: policy?.forceUpdate || false,
        isActive: policy?.isActive !== undefined ? policy.isActive : true,
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        onSave({
            platform: formData.platform,
            versionName: formData.versionName.trim(),
            buildNumber: parseInt(formData.buildNumber, 10),
            minSupportedVersionName: formData.minSupportedVersionName.trim() || null,
            minSupportedBuildNumber: parseOptionalPositiveInt(formData.minSupportedBuildNumber),
            storeUrl: formData.storeUrl.trim(),
            releaseNotes: formData.releaseNotes.trim() || null,
            forceUpdate: formData.forceUpdate,
            isActive: formData.isActive,
        })
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <form onSubmit={handleSubmit} className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto rounded bg-white p-6">
                <h2 className="text-lg font-semibold">{policy ? '앱 버전 수정' : '앱 버전 추가'}</h2>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-sm font-medium">플랫폼 *</label>
                        <select
                            required
                            className="mt-1 w-full rounded border px-3 py-2"
                            value={formData.platform}
                            onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                        >
                            {APP_PLATFORM_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">빌드 번호 *</label>
                        <input
                            type="number"
                            min="1"
                            required
                            className="mt-1 w-full rounded border px-3 py-2"
                            value={formData.buildNumber}
                            onChange={(e) => setFormData({ ...formData, buildNumber: e.target.value })}
                            placeholder="25"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-sm font-medium">버전명 *</label>
                        <input
                            type="text"
                            required
                            className="mt-1 w-full rounded border px-3 py-2"
                            value={formData.versionName}
                            onChange={(e) => setFormData({ ...formData, versionName: e.target.value })}
                            placeholder="1.0.0"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">스토어 URL *</label>
                        <input
                            type="url"
                            required
                            className="mt-1 w-full rounded border px-3 py-2"
                            value={formData.storeUrl}
                            onChange={(e) => setFormData({ ...formData, storeUrl: e.target.value })}
                            placeholder="https://play.google.com/store/apps/details?id=..."
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-sm font-medium">최소 지원 버전명</label>
                        <input
                            type="text"
                            className="mt-1 w-full rounded border px-3 py-2"
                            value={formData.minSupportedVersionName}
                            onChange={(e) => setFormData({ ...formData, minSupportedVersionName: e.target.value })}
                            placeholder="1.0.0"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">최소 지원 빌드 번호</label>
                        <input
                            type="number"
                            min="1"
                            className="mt-1 w-full rounded border px-3 py-2"
                            value={formData.minSupportedBuildNumber}
                            onChange={(e) => setFormData({ ...formData, minSupportedBuildNumber: e.target.value })}
                            placeholder="25"
                        />
                    </div>
                </div>

                <div>
                    <label className="mb-1 block text-sm font-medium">릴리스 노트</label>
                    <textarea
                        className="mt-1 w-full rounded border px-3 py-2"
                        rows="4"
                        value={formData.releaseNotes}
                        onChange={(e) => setFormData({ ...formData, releaseNotes: e.target.value })}
                        placeholder="변경 사항을 입력하세요..."
                    />
                </div>

                <div className="flex flex-wrap items-center gap-6">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.forceUpdate}
                            onChange={(e) => setFormData({ ...formData, forceUpdate: e.target.checked })}
                        />
                        <span className="text-sm">필수 업데이트</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        <span className="text-sm">활성화</span>
                    </label>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="rounded border px-4 py-2 text-sm" onClick={onClose}>
                        취소
                    </button>
                    <button type="submit" className="rounded bg-brand-navy px-4 py-2 text-sm text-white">
                        {policy ? '수정' : '생성'}
                    </button>
                </div>
            </form>
        </div>
    )
}
